import { Component, computed, inject, input, OnInit, OnDestroy, WritableSignal, signal } from "@angular/core";
import { NgClass, CommonModule } from "@angular/common";
import { rxResource } from "@angular/core/rxjs-interop";
import { map, of, Subscription } from "rxjs";
import { EnergyLabelSource, EnergyLabelColor, EnergyLabelVersion } from "src/models/common/energy.enums";
import { UnitEnergyInfo } from "src/models/energy-estimate/unit-energy-info.model";
import { EVServiceType } from "src/util/auth0/models/evServiceType.enum";
import { ExternalLinksService } from "src/services/externallinks.service";
import { DefaultConverterPipe } from "src/app/shared/pipes/defaultconverter.pipe";
import { environment } from "src/environments/environment";
import { ThousandizePipe } from "ev-core";
import { EvDirectivesModule } from "ev-directives";
import { EvTooltipModule } from "ev-tooltip";
import { EvAuthorizationService } from "src/util/auth0/ev-auth.service";
import { ShareAppartementInfoDto } from "src/models/estate/key-info.model";
import { EvCore, UnitPipe } from "ev-core";
import { Router } from "@angular/router";
import { SubAccordionComponent } from "../info-page-accordion/sub-accordion/sub-accordion.component";
import { EnergyDataService } from "src/services/energy-data.service";
import { AccordionStateClass, AccordionIdentity } from "../../models/accordion-state.class";
import { EnergyCalculationReportsComponent } from "./energy-calculation-reports/energy-calculation-reports.component";
import { EnergyLabelHistoryComponent } from "./energy-label-history/energy-label-history.component";

@Component({
    selector: "unit-energy-info",
    templateUrl: "./unit-energy-info.component.html",
    styleUrl: "./unit-energy-info.component.scss",
    imports: [
        DefaultConverterPipe,
        ThousandizePipe,
        CommonModule,
        EvDirectivesModule,
        EvTooltipModule,
        EvCore,
        SubAccordionComponent,
        EnergyCalculationReportsComponent,
        EnergyLabelHistoryComponent,
        UnitPipe,
    ],
})
export class UnitEnergyInfoComponent implements OnInit, OnDestroy {
    energyInfo = input.required<UnitEnergyInfo>();
    keyInfo = input.required<ShareAppartementInfoDto>();

    externalLinksService = inject(ExternalLinksService);
    private authService = inject(EvAuthorizationService);
    private router = inject(Router);
    private energyDataService = inject(EnergyDataService);

    showBasisTooltip = false;
    reportsExpanded = false;
    hasGreenHomeRole = false;
    hasEnergyCalculationRole = false;
    EnergyLabelVersion = EnergyLabelVersion;
    greenEnergyIconUrl = `${environment.cdnUrl}/energyresources/icon-gronnbolig.svg`;
    protected readonly energyCalculationReportsStateClass;
    protected readonly energyLabelHistoryStateClass;
    private subs: Subscription[] = [];
    private fetchEnergyCalculationReports: WritableSignal<boolean> = signal(false);
    private fetchEnergyLabelHistory: WritableSignal<boolean> = signal(false);

    isEstimated = computed(() => this.energyInfo()?.energyLabelSource == EnergyLabelSource.Simien);

    energyLabelClass = computed(() => {
        return this.energyInfo()?.energyLabel?.toLowerCase() + "-" + EnergyLabelColor[this.energyInfo()?.energyLabelColor]?.toLowerCase();
    });

    energyLabelColor = computed(() => {
        switch (this.energyInfo()?.energyLabelColor) {
            case EnergyLabelColor.Green:
                return "Grønn";
            case EnergyLabelColor.LightGreen:
                return "Lys grønn";
            case EnergyLabelColor.Yellow:
                return "Gul";
            case EnergyLabelColor.Orange:
                return "Oransje";
            case EnergyLabelColor.Red:
                return "Rød";
            default:
                return "-";
        }
    });

    energyLabelSource = computed(() => {
        switch (this.energyInfo()?.energyLabelSource) {
            case EnergyLabelSource.Other:
                return "Annet";
            case EnergyLabelSource.AdPortal:
                return "Annonseportal";
            case EnergyLabelSource.LineOfBusinessSystem:
                return "Fagsystem megler";
            case EnergyLabelSource.AdministrationSystem:
                return "Forvaltingssystem";
            case EnergyLabelSource.Enova:
                return "Enova";
            case EnergyLabelSource.Simien:
                return "Simien  ";
            default:
                return "-";
        }
    });

    energyLabelEnovaNumber = computed(() => {
        if (this.energyInfo()?.certificateNumber) {
            return this.energyInfo().certificateNumber;
        }

        return this.energyInfo()?.energyLabelEnovaNumber;
    });

    enovaLink = computed(() => {
        if (this.energyInfo()?.certificateNumberURL) {
            return this.energyInfo().certificateNumberURL;
        } else if (this.keyInfo().organizationNumber) {
            return `https://portal.ems.enova.no/sok?q=${this.keyInfo().unitAddress.replace(/[\s,]/g, "+")}`;
        } else if (
            this.keyInfo().parentCadastre?.kNr !== undefined &&
            this.keyInfo().parentCadastre?.gNr !== undefined &&
            this.keyInfo().parentCadastre?.bNr !== undefined &&
            this.keyInfo().parentCadastre?.fNr !== undefined &&
            this.keyInfo().parentCadastre?.sNr !== undefined
        ) {
            let kNrString = this.keyInfo().parentCadastre?.kNr.toString().padStart(4, "0");
            return `https://portal.ems.enova.no/sok/advanced?kommunenummer=${kNrString}&gardsnummer=${this.keyInfo().parentCadastre?.gNr}&bruksnummer=${
                this.keyInfo().parentCadastre?.bNr
            }&festenummer=${this.keyInfo().parentCadastre?.fNr}&seksjonsnummer=${this.keyInfo().parentCadastre?.sNr}`;
        } else {
            return "https://portal.eiendomsverdi.no/Home/Error?";
        }
    });

    energyLabelDescription = computed(() => {
        if (this.energyInfo()?.energyLabelVersion == EnergyLabelVersion.Version2026) {
            return this.energyInfo()?.energyLabel;
        }

        return this.energyInfo()?.energyLabel + " " + this.energyLabelColor();
    });
    private energyCalculationReportsTrigger = computed(() => ({
        unitId: this.keyInfo().unitId,
        propertyOpened: this.fetchEnergyCalculationReports(),
    }));

    energyCalculationReportsResource = rxResource({
        params: () => this.energyCalculationReportsTrigger(),
        stream: (loaderparams) => {
            if (loaderparams.params.propertyOpened && loaderparams.params.unitId) {
                return this.energyDataService.getEnergyCalculationReports(loaderparams.params.unitId);
            }

            return of(null);
        },
    });

    private energyLabelHistoryTrigger = computed(() => ({
        unitId: this.keyInfo().unitId,
        propertyOpened: this.fetchEnergyLabelHistory(),
    }));

    energyLabelHistoryResource = rxResource({
        params: () => this.energyLabelHistoryTrigger(),
        stream: (loaderparams) => {
            if (loaderparams.params.propertyOpened && loaderparams.params.unitId) {
                return this.energyDataService.getUnitEnergyLabelHistory(loaderparams.params.unitId);
            }

            return of(null);
        },
    });

    constructor() {
        this.energyCalculationReportsStateClass = new AccordionStateClass(
            this.energyCalculationReportsResource,
            "Energiberegningsrapporter",
            AccordionIdentity.SubAccordion,
            "Fant ingen lagrede energirapporter",
            "Klarte ikke å laste inn energiberegningsrapportar for boenheten",
            (expanded: boolean) => this.triggerLoadEnergyCalculationReports(),
        );
        this.energyLabelHistoryStateClass = new AccordionStateClass(
            this.energyLabelHistoryResource,
            "Energimerkehistorikk",
            AccordionIdentity.SubAccordion,
            "Fant ingen energimerkehistorikk for denne eiendommen",
            "Klarte ikke å laste inn energimerkehistorikk",
            (expanded: boolean) => this.triggerLoadEnergyLabelHistory(),
        );
    }

    ngOnInit(): void {
        this.subs.push(
            this.authService.hasPermission(EVServiceType.EvWeb_Greenhousing).subscribe((hasPermission) => {
                this.hasGreenHomeRole = hasPermission;
            }),
        );

        this.subs.push(
            this.authService.hasPermission(EVServiceType.EvWeb_EnergyCalculation).subscribe((hasPermission) => {
                this.hasEnergyCalculationRole = hasPermission;
            }),
        );
    }

    ngOnDestroy(): void {
        this.subs.forEach((s) => s.unsubscribe());
    }

    triggerLoadEnergyCalculationReports(): void {
        this.fetchEnergyCalculationReports.set(true);
    }

    triggerLoadEnergyLabelHistory(): void {
        this.fetchEnergyLabelHistory.set(true);
    }

    toggleBasisTooltip(): void {
        this.showBasisTooltip = !this.showBasisTooltip;
    }

    toggleExpandEnergyReports(): void {
        this.reportsExpanded = !this.reportsExpanded;
    }

    onPerformEnergyCalculationClick(): void {
        const currentUrl = window.location.href;

        this.router.navigate(["/energy/" + this.keyInfo().unitId], { queryParams: { returnTo: currentUrl } });
    }
}
